// @ts-nocheck
import axios from 'axios';

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  process.env.REACT_APP_API_URL ||
  'http://localhost:8080';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

const GET_CACHE_TTL = 120000;
const getCache = new Map();
const pendingGetRequests = new Map();

const PUBLIC_ROUTES = [
  '/api/auth/login',
  '/api/auth/login-otp',
  '/api/auth/send-login-otp',
  '/api/auth/signup',
  '/api/auth/password/forgot',
  '/api/auth/password/reset',
  '/api/auth/password/validate',
  '/api/verification/send-otp',
  '/api/verification/send-phone-otp',
  '/api/verification/verify-otp',
  '/api/verification/verify-phone-otp',
];

const PUBLIC_GET_ROUTES = [
  '/api/categories',
  '/api/subcategories',
  '/api/cities',
  '/api/pincodes',
  '/api/orders/track',
];

const CACHEABLE_GET_ROUTES = [
  '/api/categories',
  '/api/subcategories',
  '/api/cities',
  '/api/pincodes',
];

const isCacheableGet = (url, config = {}) => {
  if (config.forceAuth || config.skipCache) {
    return false;
  }

  return CACHEABLE_GET_ROUTES.some((route) => String(url || '').startsWith(route));
};

const buildCacheKey = (url, config = {}) => {
  const params = config.params ? JSON.stringify(config.params) : '';
  const token =
    localStorage.getItem('token') ||
    localStorage.getItem('adminToken') ||
    localStorage.getItem('authToken') ||
    '';
  const authScope = config.forceAuth || !PUBLIC_GET_ROUTES.some((route) => String(url || '').includes(route))
    ? token
    : '';

  return `${url}?${params}#${authScope}`;
};

const clearCachedGets = (url = '') => {
  const requestPath = String(url || '');
  const cacheGroups = [];

  if (requestPath.startsWith('/api/categories') || requestPath.startsWith('/api/subcategories')) {
    cacheGroups.push('/api/categories', '/api/subcategories');
  } else if (requestPath.startsWith('/api/cities')) {
    cacheGroups.push('/api/cities');
  } else if (requestPath.startsWith('/api/pincodes')) {
    cacheGroups.push('/api/pincodes');
  }

  for (const key of getCache.keys()) {
    if (!requestPath || cacheGroups.some((group) => key.startsWith(group))) {
      getCache.delete(key);
    }
  }
};

const rawGet = apiClient.get.bind(apiClient);

apiClient.get = (url, config = {}) => {
  const cacheable = isCacheableGet(url, config);
  const cacheKey = buildCacheKey(url, config);
  const cached = cacheable ? getCache.get(cacheKey) : null;

  if (cached && Date.now() - cached.timestamp < GET_CACHE_TTL) {
    return Promise.resolve(cached.response);
  }

  if (!config.skipDedupe && pendingGetRequests.has(cacheKey)) {
    return pendingGetRequests.get(cacheKey);
  }

  const request = rawGet(url, config)
    .then((response) => {
      if (cacheable) {
        getCache.set(cacheKey, {
          timestamp: Date.now(),
          response,
        });
      }
      return response;
    })
    .finally(() => {
      pendingGetRequests.delete(cacheKey);
    });

  pendingGetRequests.set(cacheKey, request);
  return request;
};

apiClient.interceptors.request.use(
  (config) => {
    const token =
      localStorage.getItem('token') ||
      localStorage.getItem('adminToken') ||
      localStorage.getItem('authToken');
    const requestPath = config.url || '';
    const requestMethod = (config.method || 'get').toLowerCase();
    const forceAuth = Boolean(config.forceAuth);
    const isPublicRoute = PUBLIC_ROUTES.some((route) => requestPath.includes(route));
    const isPublicGetRoute =
      requestMethod === 'get' &&
      PUBLIC_GET_ROUTES.some((route) => requestPath.includes(route));

    if (token && (forceAuth || (!isPublicRoute && !isPublicGetRoute))) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

apiClient.interceptors.response.use(
  (response) => {
    const method = response.config?.method?.toLowerCase();

    if (method && method !== 'get') {
      clearCachedGets(response.config?.url);
    }

    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      localStorage.clear();

      if (window.location.hash !== '#/login') {
        window.location.hash = '#/login';
      }
    }

    const responseData = error.response?.data;
    if (typeof responseData === 'string') {
      return Promise.reject(Object.assign(new Error(responseData), { response: error.response }));
    }
    if (responseData?.error) {
      return Promise.reject(Object.assign(new Error(responseData.error), { response: error.response }));
    }
    if (responseData?.message) {
      return Promise.reject(Object.assign(new Error(responseData.message), { response: error.response }));
    }

    return Promise.reject(error);
  }
);
export default apiClient;
